/**
 * K 線資料診斷（僅 debug log，不影響業務邏輯）。
 * 開發環境預設開啟；正式環境設 REACT_APP_CHART_DEBUG=true 可強制開啟。
 */
export function isChartDebugEnabled() {
  return process.env.NODE_ENV === 'development' || process.env.REACT_APP_CHART_DEBUG === 'true';
}

export function barTimeKey(bar) {
  if (!bar) {
    return '';
  }
  if (bar.timestamp != null && !Number.isNaN(Number(bar.timestamp))) {
    return String(Number(bar.timestamp));
  }
  if (bar.openTime) {
    const parsed = Date.parse(bar.openTime);
    return Number.isNaN(parsed) ? String(bar.openTime) : String(parsed);
  }
  return '';
}

function readPrices(bar) {
  return {
    open: Number(bar?.open),
    high: Number(bar?.high),
    low: Number(bar?.low),
    close: Number(bar?.close),
  };
}

function isInvalidOhlc(p) {
  if ([p.open, p.high, p.low, p.close].some(Number.isNaN)) {
    return true;
  }
  return (
    p.high < p.low
    || p.open > p.high
    || p.open < p.low
    || p.close > p.high
    || p.close < p.low
  );
}

function bodyRatio(p) {
  const spread = p.high - p.low;
  if (spread <= 0) {
    return null;
  }
  return Math.abs(p.open - p.close) / spread;
}

/**
 * 稽核 K 線陣列並輸出診斷報告。
 */
export function auditBars(bars, label = 'bars', extra = {}) {
  if (!isChartDebugEnabled()) {
    return null;
  }

  const list = Array.isArray(bars) ? bars : [];
  const timestampKeys = {};
  const minuteKeys = {};
  let invalidOhlcCount = 0;
  let zeroBodyCount = 0;
  let fullBodyCount = 0;
  let openEqHighCloseEqLowCount = 0;
  const invalidSamples = [];

  let minLow = Infinity;
  let maxHigh = -Infinity;

  list.forEach((bar, index) => {
    const key = barTimeKey(bar);
    if (key) {
      timestampKeys[key] = (timestampKeys[key] || 0) + 1;
    }
    if (bar?.openTime) {
      const minute = String(bar.openTime).slice(0, 16);
      minuteKeys[minute] = (minuteKeys[minute] || 0) + 1;
    }

    const p = readPrices(bar);
    if ([p.open, p.high, p.low, p.close].some(Number.isNaN)) {
      invalidOhlcCount += 1;
      if (invalidSamples.length < 5) {
        invalidSamples.push({ index, reason: 'NaN', bar });
      }
      return;
    }

    if (!Number.isNaN(p.low)) {
      minLow = Math.min(minLow, p.low);
    }
    if (!Number.isNaN(p.high)) {
      maxHigh = Math.max(maxHigh, p.high);
    }

    if (isInvalidOhlc(p)) {
      invalidOhlcCount += 1;
      if (invalidSamples.length < 5) {
        invalidSamples.push({ index, reason: 'OHLC violation', open: p.open, high: p.high, low: p.low, close: p.close, openTime: bar.openTime });
      }
    }

    if (p.open === p.close) {
      zeroBodyCount += 1;
    }

    const ratio = bodyRatio(p);
    if (ratio != null && ratio >= 0.95) {
      fullBodyCount += 1;
    }

    if (p.open === p.high && p.close === p.low && p.high > p.low) {
      openEqHighCloseEqLowCount += 1;
    }
  });

  const dupTimestampCount = Object.values(timestampKeys).filter((n) => n > 1).length;
  const dupMinuteCount = Object.values(minuteKeys).filter((n) => n > 1).length;
  const priceSpread = Number.isFinite(minLow) && Number.isFinite(maxHigh) ? maxHigh - minLow : null;

  const report = {
    label,
    total: list.length,
    dupTimestampCount,
    dupMinuteCount,
    invalidOhlcCount,
    zeroBodyCount,
    fullBodyCount,
    openEqHighCloseEqLowCount,
    minLow: Number.isFinite(minLow) ? minLow : null,
    maxHigh: Number.isFinite(maxHigh) ? maxHigh : null,
    priceSpread,
    sampleFirst5: list.slice(0, 5).map((bar) => {
      const p = readPrices(bar);
      return {
        openTime: bar.openTime,
        timestamp: bar.timestamp,
        overridden: bar.overridden,
        ...p,
        bodyRatio: bodyRatio(p),
      };
    }),
    sampleLast5: list.slice(-5).map((bar) => {
      const p = readPrices(bar);
      return {
        openTime: bar.openTime,
        timestamp: bar.timestamp,
        overridden: bar.overridden,
        ...p,
        bodyRatio: bodyRatio(p),
      };
    }),
    invalidSamples,
    ...extra,
  };

  console.group(`[ChartDebug] auditBars — ${label}`);
  console.log(report);
  if (dupTimestampCount > 0) {
    console.warn('[ChartDebug] duplicated timestamp keys', Object.entries(timestampKeys).filter(([, n]) => n > 1).slice(0, 10));
  }
  if (invalidOhlcCount > 0) {
    console.warn('[ChartDebug] invalid OHLC samples', invalidSamples);
  }
  if (openEqHighCloseEqLowCount > 0) {
    console.warn(`[ChartDebug] open=high & close=low count=${openEqHighCloseEqLowCount} (長直線特徵)`);
  }
  console.groupEnd();

  return report;
}

/**
 * 比對兩批 K 線資料差異（用於 raw api vs chart data）。
 */
export function diffBarAudits(baseline, current, baselineLabel = 'raw api', currentLabel = 'chart data') {
  if (!isChartDebugEnabled()) {
    return null;
  }
  const a = auditBars(baseline, baselineLabel);
  const b = auditBars(current, currentLabel);
  if (!a || !b) {
    return null;
  }
  const summary = {
    baselineTotal: a.total,
    currentTotal: b.total,
    openEqHighCloseEqLowDelta: b.openEqHighCloseEqLowCount - a.openEqHighCloseEqLowCount,
    invalidOhlcDelta: b.invalidOhlcCount - a.invalidOhlcCount,
    fullBodyDelta: b.fullBodyCount - a.fullBodyCount,
    priceSpreadBaseline: a.priceSpread,
    priceSpreadCurrent: b.priceSpread,
  };
  console.group('[ChartDebug] diffBarAudits');
  console.log(summary);
  console.groupEnd();
  return summary;
}

/**
 * 圖表 Y 軸與渲染上下文診斷。
 */
export function auditRenderContext(visibleBars, currentPrice, extra = {}) {
  if (!isChartDebugEnabled()) {
    return null;
  }

  let minPrice = Infinity;
  let maxPrice = -Infinity;
  visibleBars.forEach((bar) => {
    const p = readPrices(bar);
    if (!Number.isNaN(p.low)) {
      minPrice = Math.min(minPrice, p.low);
    }
    if (!Number.isNaN(p.high)) {
      maxPrice = Math.max(maxPrice, p.high);
    }
  });

  const livePrice = Number(currentPrice);
  const liveInRange = Number.isFinite(minPrice) && Number.isFinite(maxPrice) && !Number.isNaN(livePrice)
    ? livePrice >= minPrice && livePrice <= maxPrice
    : null;

  let yMinBeforePadding = minPrice;
  let yMaxBeforePadding = maxPrice;
  if (!Number.isNaN(livePrice)) {
    yMinBeforePadding = Math.min(yMinBeforePadding, livePrice);
    yMaxBeforePadding = Math.max(yMaxBeforePadding, livePrice);
  }

  const spread = yMaxBeforePadding - yMinBeforePadding;
  const padding = spread > 0 ? spread * 0.06 : Math.max(yMaxBeforePadding, 1) * 0.001;
  const yMin = yMinBeforePadding - padding;
  const yMax = yMaxBeforePadding + padding;
  const yRange = yMax - yMin || 1;

  const report = {
    renderComponent: 'FuturesCandlestickChart (Canvas 2D)',
    visibleBarCount: visibleBars.length,
    yMin,
    yMax,
    yRange,
    yMinIsFixed: false,
    yMaxIsFixed: false,
    currentPrice: Number.isNaN(livePrice) ? null : livePrice,
    currentPriceInBarRange: liveInRange,
    currentPriceStretchRisk: liveInRange === false,
    ...extra,
  };

  console.group('[ChartDebug] auditRenderContext');
  console.log(report);
  if (liveInRange === false) {
    console.warn('[ChartDebug] currentPrice 超出 bars 價格區間，可能壓扁 K 線');
  }
  console.groupEnd();

  return report;
}
